import React from 'react';
import {
  AgeIcon,
  AutoImmuneIcon,
  BloodBiomarkersIcon,
  BloodTypeIcon,
  CancerIcon,
  CoronaryIcon,
  DiabetesIcon,
  HeartIcon,
  KidneyLiverIcon,
  LungsIcon,
  LowLikelihoodIcon,
  SignificantLikelihoodIcon,
  HighLikelihoodIcon,
} from 'on_web/assets/iconsList';
import i18n from 'i18n';
import {
  dnaBasedLevelLow,
  dnaBasedLevelModerate,
  dnaBasedLevelHigh,
  dnaBasedUnknown,
  LowLikelihoodBar,
  SignificantLikelihoodBar,
  HighLikelihoodBar,
  healthBasedLevelLow,
  healthBasedLevelSevere,
  healthBasedLevelCritical,
  recommendationLowRisk,
  recommendationModerateRisk,
  recommendationCriticalRisk,
} from 'on_web/assets/imageList';
import {
  LOW_RISK_COLOR,
  MODERATE_RISK_COLOR,
  HIGH_RISK_COLOR,
  SEVERE_RISK_COLOR,
  CRITICAL_RISK_COLOR,
  UNKNOWN_RISK_COLOR,
} from 'colours';

export const healthRiskGroups = i18n.t('reporting.common.comorbidities', {
  returnObjects: true,
});

export const getHealthRisks = (iconColor, iconSize) => {
  return [
    {
      code: 'age',
      icon: <AgeIcon fill={iconColor} width={iconSize} height={iconSize} />,
    },
    {
      code: 'respiratory',
      icon: <LungsIcon fill={iconColor} width={iconSize} height={iconSize} />,
    },
    {
      code: 'cardiac',
      icon: (
        <CoronaryIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
    },
    {
      code: 'bmi_diabetes',
      icon: (
        <DiabetesIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
    },
    {
      code: 'kidney',
      icon: (
        <KidneyLiverIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
    },
    {
      code: 'autoimmune',
      icon: (
        <AutoImmuneIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
    },
    {
      code: 'cancer',
      icon: <CancerIcon fill={iconColor} width={iconSize} height={iconSize} />,
    },
  ];
};

export const getComorbidityNameByCode = (code, healthBasedSeverity) => {
  return healthBasedSeverity
    ? healthBasedSeverity.groups.find(g => g.code === code).name
    : '';
};

export const dnaTraits = i18n.t('reporting.common.traits', {
  returnObjects: true,
});

export const getDnaRisks = (iconColor, iconSize) => {
  return [
    {
      code: 'respiratory',
      icon: <LungsIcon fill={iconColor} width={iconSize} height={iconSize} />,
      traits: [],
      alwaysVisible: false,
    },
    {
      code: 'cardiac',
      icon: <HeartIcon fill={iconColor} width={iconSize} height={iconSize} />,
      traits: [],
      alwaysVisible: false,
    },
    {
      code: 'diabetes',
      icon: (
        <DiabetesIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
      traits: [],
      alwaysVisible: false,
    },
    {
      code: 'blood_biomarkers',
      icon: (
        <BloodBiomarkersIcon
          fill={iconColor}
          width={iconSize}
          height={iconSize}
        />
      ),
      traits: [],
      alwaysVisible: true,
    },
    {
      code: 'blood_type',
      icon: (
        <BloodTypeIcon fill={iconColor} width={iconSize} height={iconSize} />
      ),
      traits: [],
      alwaysVisible: true,
    },
  ];
};

export const getDnaRiskNameByCode = code => {
  if (code === 'blood_biomarkers') {
    return 'Blood biomarkers';
  } else if (code === 'blood_type') {
    return 'Blood type';
  } else {
    return i18n.t('reporting.common.dnaCategories.' + code);
  }
};

export const getHealthRiskScore = healthBasedRisks => {
  if (healthBasedRisks && healthBasedRisks.total_risk) {
    return healthBasedRisks.total_risk.score_percentage;
  } else {
    return 0;
  }
};

export const getHealthRiskScorePercentage = healthRiskScore => {
  return healthRiskScore < 10 ? '<10%' : healthRiskScore + '%';
};

export const parseLikelihood = (infectionLikelihood, t) => {
  let likelihood = {};

  likelihood.level = infectionLikelihood.total_risk.level;
  likelihood.levelText = infectionLikelihood.total_risk.level_label;
  likelihood.percentage = infectionLikelihood.total_risk.score_percentage;
  likelihood.scoreDescription = [
    infectionLikelihood.total_risk.level_description,
  ];
  likelihood.disclaimer = t(
    'reporting.myAssessment.likelihoodHistoryDisclaimer'
  );
  likelihood.disclaimer2 = t(
    'reporting.myAssessment.likelihoodHistoryDisclaimer2'
  );
  likelihood.testbasedInfection = {
    percentage: infectionLikelihood.risks[0].score_percentage + '%',
    name: infectionLikelihood.risks[0].risk.name,
    accuracy: infectionLikelihood.risks[0].level_description,
    level: infectionLikelihood.risks[0].level,
  };
  likelihood.antiBodyTestBasedInfection = {
    percentage: infectionLikelihood.risks[1].score_percentage + '%',
    name: infectionLikelihood.risks[1].risk.name,
    accuracy: infectionLikelihood.risks[1].level_description,
    level: infectionLikelihood.risks[1].level,
  };
  likelihood.symptomBasedInfection = {
    percentage: infectionLikelihood.risks[2].score_percentage + '%',
    name: infectionLikelihood.risks[2].risk.name,
    accuracy: infectionLikelihood.risks[2].level_description,
    level: infectionLikelihood.risks[2].level,
  };
  likelihood.color =
    likelihood.level === 0
      ? LOW_RISK_COLOR
      : likelihood.level === 1
      ? MODERATE_RISK_COLOR
      : HIGH_RISK_COLOR;
  const height = 190;
  likelihood.illustration =
    likelihood.level === 0 ? (
      <LowLikelihoodIcon percentage={likelihood.percentage} height={height} />
    ) : likelihood.level === 1 ? (
      <SignificantLikelihoodIcon
        percentage={likelihood.percentage}
        height={height}
      />
    ) : (
      <HighLikelihoodIcon percentage={likelihood.percentage} height={height} />
    );
  likelihood.bar =
    likelihood.level === 0 ? (
      <LowLikelihoodBar />
    ) : likelihood.level === 1 ? (
      <SignificantLikelihoodBar />
    ) : (
      <HighLikelihoodBar />
    );
  return likelihood;
};

/**
 * Parses specified object with health risks returning object containing
 */
export const parseHealthRisks = healthRisks => {
  if (healthRisks == null || healthRisks.total_risk == null) {
    return;
  }

  const riskLevel = healthRisks.total_risk.level;

  if (riskLevel === 0)
    return {
      text: healthRisks.total_risk.level_label,
      textColor: LOW_RISK_COLOR,
      image: healthBasedLevelLow,
    };
  if (riskLevel === 1)
    return {
      text: healthRisks.total_risk.level_label,
      textColor: SEVERE_RISK_COLOR,
      image: healthBasedLevelSevere,
    };
  if (riskLevel === 2)
    return {
      text: healthRisks.total_risk.level_label,
      textColor: CRITICAL_RISK_COLOR,
      image: healthBasedLevelCritical,
    };
};

export const getUnknownDnaRisk = t => {
  return {
    value: -1,
    text: t('reporting.myAssessment.unknown'),
    textColor: UNKNOWN_RISK_COLOR,
    image: dnaBasedUnknown,
  };
};

export const parseDnaTotalRisk = (totalRiskObj, t) => {
  if (totalRiskObj == null || totalRiskObj.total_risk == null) {
    return getUnknownDnaRisk(t);
  }
  const riskValue = totalRiskObj.total_risk.level;
  const riskText = totalRiskObj.total_risk.level_label;

  if (riskValue === 0)
    return {
      value: riskValue,
      text: riskText,
      textColor: LOW_RISK_COLOR,
      image: dnaBasedLevelLow,
    };
  else if (riskValue === 1)
    return {
      value: riskValue,
      text: riskText,
      textColor: MODERATE_RISK_COLOR,
      image: dnaBasedLevelModerate,
    };
  else if (riskValue === 2)
    return {
      value: riskValue,
      text: riskText,
      textColor: HIGH_RISK_COLOR,
      image: dnaBasedLevelHigh,
    };
  else return getUnknownDnaRisk(t);
};

export const getRecommendation = healthRisks => {
  if (
    healthRisks == null ||
    healthRisks.total_risk == null ||
    healthRisks.recommendations == null
  ) {
    return;
  }

  const riskLevel = healthRisks.total_risk.level;

  if (riskLevel === 0) {
    return {
      image: recommendationLowRisk,
      color: LOW_RISK_COLOR,
      text: healthRisks.recommendations.summary[0],
      tips: healthRisks.recommendations.tips,
    };
  } else if (riskLevel === 1) {
    return {
      image: recommendationModerateRisk,
      color: SEVERE_RISK_COLOR,
      text: healthRisks.recommendations.summary[0],
      tips: healthRisks.recommendations.tips,
    };
  } else if (riskLevel === 2) {
    return {
      image: recommendationCriticalRisk,
      color: CRITICAL_RISK_COLOR,
      text: healthRisks.recommendations.summary[0],
      tips: healthRisks.recommendations.tips,
    };
  }
};

export const userCanSeeDnaContent = groups => {
  if (!groups) {
    return false;
  }

  return (
    groups.includes('spare-dna-inapp-purchase-users') ||
    groups.includes('spare-dna-demo-users')
  );
};

export const userCanSeeFullDna = groups => {
  if (!groups) {
    return false;
  }

  return groups.includes('spare-dna-demo-users');
};

export const isDnaUploaded = genotypeData => {
  return genotypeData != null && genotypeData.genotype_status != null;
};

export const isImputationCompleted = genotypeData => {
  if (!genotypeData) {
    return false;
  }

  return genotypeData.imputed_genotype_status === 'imputation-completed';
};

export const isGenotypeInvalid = genotypeData => {
  if (!genotypeData) {
    return false;
  }

  return genotypeData.genotype_status === 'genotype-data-invalid';
};

export const isImputationInError = genotypeData => {
  if (!genotypeData) {
    return false;
  }

  return genotypeData.imputed_genotype_status === 'imputation-error';
};

export const isImputationInProgress = genotypeData => {
  if (!genotypeData) {
    return false;
  }

  return (
    genotypeData.genotype_status === 'genotype-data-not-available' ||
    genotypeData.genotype_status === 'genotype-data-available' ||
    genotypeData.genotype_status === 'datasource-invalid' ||
    genotypeData.imputed_genotype_status === 'imputation-in-progress' ||
    (genotypeData.genotype_status === 'trait-scores-available' &&
      genotypeData.imputed_genotype_status !== 'imputation-completed' &&
      genotypeData.imputed_genotype_status !== 'imputation-error') ||
    (genotypeData.genotype_status === 'genotype-data-valid' &&
      genotypeData.imputed_genotype_status !== 'imputation-completed' &&
      genotypeData.imputed_genotype_status !== 'imputation-error')
  );
};

export const canUserSeeFullDna = groups => {
  if (!groups) {
    return false;
  }

  if (
    groups.find(
      group => group === 'spare-dna-users' || group === 'spare-dna-demo-users'
    )
  ) {
    return true;
  } else {
    return false;
  }
};
